import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
    ArrowRight,
    CheckCircle,
    Clock,
    Shield,
    FileCheck,
    Users,
    RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// Hero image for this page
const HERO_IMAGE = "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=80";

const TilitoimistonVaihtaminen = () => {
    const steps = [
        {
            number: "1",
            title: "Ota yhteyttä",
            description: "Kerro meille nykytilanteestasi ja tarpeistasi. Kartoitamme yhdessä parhaan ratkaisun."
        },
        {
            number: "2",
            title: "Suunnitellaan vaihto",
            description: "Laadimme selkeän suunnitelman siirtymälle. Sovitaan aikataulut ja vastuut."
        },
        {
            number: "3",
            title: "Tietojen siirto",
            description: "Siirrämme kirjanpitoaineiston turvallisesti. Hoidamme kommunikaation edellisen tilitoimiston kanssa."
        },
        {
            number: "4",
            title: "Sujuva aloitus",
            description: "Aloitamme kirjanpidon ilman katkoksia. Olet hyvissä käsissä alusta alkaen."
        }
    ];

    const benefits = [
        {
            icon: Clock,
            title: "Nopea siirtymä",
            description: "Vaihto sujuu nopeasti ja vaivattomasti"
        },
        {
            icon: Shield,
            title: "Turvallinen tiedonsiirto",
            description: "Aineistosi siirtyy turvallisesti"
        },
        {
            icon: FileCheck,
            title: "Ei katkoksia",
            description: "Kirjanpito jatkuu keskeytyksettä"
        },
        {
            icon: Users,
            title: "Henkilökohtainen palvelu",
            description: "Oma yhteyshenkilö koko prosessin ajan"
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative bg-[#1a1a1a] text-white overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] to-[#1a1a1a]/80"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="text-sm uppercase tracking-[0.2em] font-medium text-green-400 mb-4 block">
                                Ratkaisut
                            </span>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
                                Tilitoimiston vaihtaminen
                            </h1>
                            <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl">
                                Tilitoimiston vaihtaminen on helpompaa kuin luulet. 
                                Me hoidamme kaiken puolestasi - sinun tarvitsee vain nauttia 
                                paremmasta palvelusta.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/#yhteystiedot">
                                    <Button className="bg-white text-[#1a1a1a] hover:bg-slate-100 px-8 py-3 rounded-full text-base">
                                        Aloita vaihto
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="hidden lg:block"
                        >
                            <img 
                                src={HERO_IMAGE}
                                alt="Tilitoimiston vaihtaminen"
                                className="rounded-2xl shadow-2xl"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Steps Section */}
            <section className="py-20 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-4">
                            Näin vaihto sujuu
                        </h2>
                        <p className="text-slate-600 max-w-2xl mx-auto">
                            Neljä helppoa askelta parempaan palveluun
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {steps.map((step, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className="h-full border-0 bg-white relative overflow-hidden">
                                    <CardContent className="p-6">
                                        <div className="text-6xl font-bold text-[#22C55E]/10 absolute top-2 right-4">
                                            {step.number}
                                        </div>
                                        <div className="relative z-10">
                                            <div className="bg-[#22C55E] w-10 h-10 rounded-full flex items-center justify-center mb-4 text-white font-semibold">
                                                {step.number}
                                            </div>
                                            <h3 className="text-xl font-semibold text-slate-800 mb-2">
                                                {step.title}
                                            </h3>
                                            <p className="text-slate-600">
                                                {step.description}
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-4">
                            Miksi vaihtaa meille?
                        </h2>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {benefits.map((benefit, index) => {
                            const Icon = benefit.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    className="text-center"
                                >
                                    <div className="bg-[#F0FDF4] w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <Icon className="h-8 w-8 text-[#22C55E]" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm">
                                        {benefit.description}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-[#1a1a1a]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <RefreshCw className="h-12 w-12 text-white/50 mx-auto mb-6" />
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-6">
                            Valmis vaihtamaan?
                        </h2>
                        <p className="text-white/80 mb-8 max-w-2xl mx-auto">
                            Ota yhteyttä ja aloitetaan keskustelu. Kerro nykytilanteestasi ja 
                            selvitetään, miten voimme auttaa sinua.
                        </p>
                        <Link to="/#yhteystiedot">
                            <Button className="bg-white text-[#1a1a1a] hover:bg-slate-100 px-8 py-3 rounded-full text-base">
                                Ota yhteyttä
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default TilitoimistonVaihtaminen;
